import { createContext, Dispatch, ReactNode, SetStateAction, useContext, useState, useEffect, useRef } from 'react'
import { AppContext } from './app.ctx'
import Profile, { ProfileFormPayment, WebhooksAsaas } from '@whatsmenu/entities/dist/profile'
import { AxiosResponse } from 'axios'

interface DataResponseType extends AxiosResponse {
  error?: boolean
}
interface PaymentMethodData {
  profileState: Profile | null
  showFinPassModal: boolean | null
  showSpinner: boolean | null
  dataToBeUpdated: any
  dataResponse: DataResponseType | null
  setDataResponse: Dispatch<SetStateAction<DataResponseType | null>>
  setDataToBeUpdated: Dispatch<SetStateAction<any | null>>
  setUpdateDataCallback: Dispatch<SetStateAction<(data: any) => any>>
  updateDataCallback: ((data: any) => any) | undefined
  showResponseAlert: boolean
  setShowResponseAlert: Dispatch<SetStateAction<boolean>>
  handleDataToBeUpdated: (data: any) => void
  handleProfileUpdate: (profile: Profile) => void
  toggleModal: (boolean: boolean) => void
  toggleSpinner: (boolean: boolean) => void
  onSubmit: (data: any, toggleSpinner: any, reset: any) => void
  allowRefundLoading: boolean
  setAllowRefundLoading: (value: boolean) => void
  webhooksStatus: WebhooksAsaas[] | null
  setWebhooksStatus: Dispatch<SetStateAction<WebhooksAsaas[] | null>>
}

interface PaymentMethodProviderProps {
  children: ReactNode
}

export const PaymentMethodContext = createContext<PaymentMethodData>({} as PaymentMethodData)

export const PaymentMethodProvider = ({ children }: PaymentMethodProviderProps) => {
  const { profile, setProfile, handleShowToast } = useContext(AppContext)
  const [showFinPassModal, setShowFinPassModal] = useState(false)
  const [showSpinner, setShowSpinner] = useState(false)
  const [dataToBeUpdated, setDataToBeUpdated] = useState<any>(null)
  const [profileState, setProfileState] = useState(profile)
  /** Evita que profileState seja sobrescrito pelo profile antigo no mesmo ciclo (eco do setProfile). */
  const lastProfilePushedToAppRef = useRef<Profile | null>(null)
  const [dataResponse, setDataResponse] = useState<DataResponseType | null>(null)
  const [updateDataCallback, setUpdateDataCallback] = useState<(data: any) => any>()
  const [showResponseAlert, setShowResponseAlert] = useState(false)
  const [allowRefundLoading, setAllowRefundLoading] = useState(false)
  const [webhooksStatus, setWebhooksStatus] = useState<WebhooksAsaas[] | null>(null)

  const handleProfileUpdate = (profile: Profile) => {
    setProfileState(profile)
  }

  const handleDataToBeUpdated = (data: any) => {
    setDataToBeUpdated(data)
  }

  const toggleModal = (boolean: boolean) => {
    setShowFinPassModal(boolean)
  }

  const toggleSpinner = (boolean: boolean) => {
    setShowSpinner(boolean)
  }

  const onSubmit = async (data: any, setShowSpinner: Dispatch<SetStateAction<boolean>>, reset: any) => {
    setShowSpinner(true)

    const settings = profileState?.formsPayment?.find((method) => method.payment === data.payment)
    if (!settings)
      return handleShowToast({
        type: 'erro',
        title: 'Forma de Pagamento',
      })

    if (['credit', 'debit', 'snack', 'food'].includes(settings.payment)) reset()

    const body: ProfileFormPayment = {
      payment: settings?.payment,
      label: settings?.label,
      status: data.status,
      statusPdv: data.statusPdv ?? data.status,
      addon: data.addon,
    }

    if ('key' in data) body.key = data.key
    if ('flags' in data) body.flags = data.flags
    if ('newFlag' in data && settings?.flags && data.newFlag && body.flags) body.flags = [...body.flags, { code: '', image: '', name: data.newFlag }]

    if (data.key?.type === 'contact') {
      data.key.value = data.key.value.replace(/\W/g, '')
    }

    setDataToBeUpdated(body)
    setShowSpinner(false)
  }

  useEffect(() => {
    lastProfilePushedToAppRef.current = profileState
    setProfile(profileState)
  }, [profileState, setProfile])

  useEffect(() => {
    if (profile != null && profile === lastProfilePushedToAppRef.current) {
      return
    }
    setProfileState(profile)
  }, [profile])

  return (
    <PaymentMethodContext.Provider
      value={{
        profileState,
        showFinPassModal,
        showSpinner,
        dataToBeUpdated,
        setDataToBeUpdated,
        toggleModal,
        toggleSpinner,
        handleProfileUpdate,
        handleDataToBeUpdated,
        onSubmit,
        dataResponse,
        setDataResponse,
        setUpdateDataCallback,
        updateDataCallback,
        setShowResponseAlert,
        showResponseAlert,
        allowRefundLoading,
        setAllowRefundLoading,
        webhooksStatus,
        setWebhooksStatus,
      }}
    >
      {children}
    </PaymentMethodContext.Provider>
  )
}
